import { KnowledgeItem } from '../../types/learning';
import { isBefore, isEqual, startOfDay } from 'date-fns';
import { createInterleaveSession, InterleaveOptions } from './interleaveScheduler';

/**
 * Configuration options for daily training sessions
 */
export interface DailyTrainingOptions {
  /** Maximum number of items in the session (default: 15) */
  maxSize?: number;
  /** Whether to use interleaved practice with balanced item types (default: true) */
  useInterleaving?: boolean;
  /** Ratio of coordinate challenges when interleaving (default: 0.4) */
  coordinateRatio?: number;
  /** Ratio of move challenges when interleaving (default: 0.4) */
  moveRatio?: number;
  /** Ratio of sequence challenges when interleaving (default: 0.2) */
  sequenceRatio?: number;
  /** Whether to include new content (default: true) */
  includeNewContent?: boolean;
}

// Default configuration for daily training sessions
const DEFAULT_DAILY_TRAINING_OPTIONS: Required<DailyTrainingOptions> = {
  maxSize: 15,
  useInterleaving: true,
  coordinateRatio: 0.4,
  moveRatio: 0.4,
  sequenceRatio: 0.2,
  includeNewContent: true
};

/**
 * Generates a daily training session of knowledge items due for review.
 * Can use either simple filtering or advanced interleaved practice with sequence challenges.
 * 
 * @param allItems All knowledge items available for review
 * @param options Optional session configuration
 * @returns Array of KnowledgeItems for the daily session, optimally ordered
 */
export function generateDailySession(
  allItems: KnowledgeItem[],
  options: DailyTrainingOptions = {}
): KnowledgeItem[] {
  // Merge with defaults
  const config = { ...DEFAULT_DAILY_TRAINING_OPTIONS, ...options };
  
  if (config.useInterleaving) {
    // Use the sophisticated interleave scheduler for balanced practice
    const interleaveOptions: InterleaveOptions = {
      coordinateRatio: config.coordinateRatio,
      moveRatio: config.moveRatio,
      sequenceRatio: config.sequenceRatio,
      includeNewContent: config.includeNewContent,
      sessionLength: config.maxSize
    };
    
    return createInterleaveSession(allItems, interleaveOptions);
  } else {
    // Fallback to simple due-date filtering (legacy behavior)
    return generateSimpleDailySession(allItems, { maxSize: config.maxSize });
  }
}

/**
 * Legacy simple daily session generation (for backward compatibility)
 * Filters items where nextReviewDate is today or earlier, sorts by nextReviewDate, and limits by maxSize.
 * 
 * @param allItems All knowledge items available for review
 * @param options Optional session options (e.g., maxSize)
 * @returns Array of KnowledgeItems due for review today, sorted and limited
 */
export function generateSimpleDailySession(
  allItems: KnowledgeItem[],
  options?: { maxSize?: number }
): KnowledgeItem[] {
  const today = startOfDay(new Date());
  // Filter items due for review (nextReviewDate today or earlier)
  const dueItems = allItems.filter(item => {
    const reviewDate = startOfDay(item.nextReviewDate);
    return isBefore(reviewDate, today) || isEqual(reviewDate, today);
  });
  // Sort by nextReviewDate ascending
  dueItems.sort((a, b) => a.nextReviewDate.getTime() - b.nextReviewDate.getTime());
  // Limit by maxSize if provided
  if (options?.maxSize && dueItems.length > options.maxSize) {
    return dueItems.slice(0, options.maxSize);
  }
  return dueItems;
}

/**
 * Creates a daily session focused on sequence challenges
 * Useful for users who want to practice tactical patterns specifically
 * 
 * @param allItems All knowledge items available for review
 * @param sessionLength Number of items to include (default: 10)
 * @returns Array of sequence-focused KnowledgeItems
 */
export function generateSequenceFocusedSession(
  allItems: KnowledgeItem[],
  sessionLength: number = 10
): KnowledgeItem[] {
  const interleaveOptions: InterleaveOptions = {
    coordinateRatio: 0.1,  // Minimal coordinate practice
    moveRatio: 0.1,        // Minimal move practice  
    sequenceRatio: 0.8,    // Focus on sequences
    includeNewContent: true,
    sessionLength
  };
  
  return createInterleaveSession(allItems, interleaveOptions);
}

/**
 * Gets session statistics for analytics and user feedback
 * 
 * @param session The generated session
 * @returns Statistics about the session composition
 */
export function getSessionStatistics(session: KnowledgeItem[]) {
  const stats = {
    total: session.length,
    coordinate: 0,
    move: 0,
    sequence: 0,
    memory: 0,
    newItems: 0,
    reviewItems: 0
  };
  
  session.forEach(item => {
    stats[item.type]++;
    if (item.repetition === 0) {
      stats.newItems++;
    } else {
      stats.reviewItems++;
    }
  });
  
  return stats;
} 